import { useCallback, useEffect, useRef } from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { getMauiIdFromAdfNode } from '@atlassian/malleable-ui-components/editor-remix/remix-ids';
import type { InsertedContentIdentity } from '@atlassian/malleable-ui-components/types';

import type { CwrRemixEditorAccess } from './types/cwr-remix-result';

const findResultPosition = (
	editorView: EditorView,
	identity: InsertedContentIdentity,
): number | undefined => {
	let resultPosition: number | undefined;
	editorView.state.doc.descendants((node, position) => {
		const matchesIdentity =
			identity.attribute === 'mauiVersion'
				? node.attrs.localId === identity.localId && getMauiIdFromAdfNode(node) === identity.value
				: node.attrs[identity.attribute] === identity.value;
		if (matchesIdentity) {
			resultPosition = position;
			return false;
		}
		return resultPosition === undefined;
	});
	return resultPosition;
};

const getResultElement = (
	editorView: EditorView,
	identity: InsertedContentIdentity,
): HTMLElement | undefined => {
	const position = findResultPosition(editorView, identity);
	if (position === undefined) {
		return undefined;
	}

	// Image media nodes render inside mediaSingle, whose DOM node is the stable viewport target.
	const dom = editorView.nodeDOM(
		identity.attribute === 'id' && position > 1 ? position - 1 : position,
	);
	return dom instanceof HTMLElement ? dom : undefined;
};

export const createCwrRemixEditorAccess = (
	editorView: EditorView | undefined,
): CwrRemixEditorAccess | undefined => {
	if (!editorView) {
		return undefined;
	}

	return {
		getEditorRoot: () => editorView.dom,
		getResultElement: (identity) => getResultElement(editorView, identity),
	};
};

const getTrackingKey = (identity: InsertedContentIdentity): string =>
	identity.attribute === 'mauiVersion'
		? `${identity.attribute}:${identity.localId}:${identity.value}`
		: `${identity.attribute}:${identity.value}`;

const PENDING_RESULT_TIMEOUT_MS = 30_000;

// This outlives an AIFC preview mount. Replaying the same SSE action after reopening a preview
// must not emit a second applied denominator for the same generated result.
const emittedResultKeys = new Set<string>();

const claimResult = (
	singleInstrumentationId: string,
	identity: InsertedContentIdentity,
): boolean => {
	const resultKey = `${singleInstrumentationId}:${getTrackingKey(identity)}`;
	if (emittedResultKeys.has(resultKey)) {
		return false;
	}
	emittedResultKeys.add(resultKey);
	return true;
};

type TrackCwrRemixWhenVisible = (
	singleInstrumentationId: string,
	identity: InsertedContentIdentity,
	onVisible: () => void,
) => void;

type PendingResult = {
	singleInstrumentationId: string;
	identity: InsertedContentIdentity;
	onVisible: () => void;
};

/**
 * Defers CWR Remix acceptance tracking until the inserted result becomes visible in the editor.
 * Existing inline Remix tracking does not use this hook.
 */
export const useCwrRemixViewportTracking = (
	getCwrRemixEditorAccess?: () => CwrRemixEditorAccess | undefined,
): TrackCwrRemixWhenVisible => {
	const getCwrRemixEditorAccessRef = useRef(getCwrRemixEditorAccess);
	const observersRef = useRef(new Map<string, IntersectionObserver>());
	const mutationObserverRef = useRef<MutationObserver | undefined>(undefined);
	const pendingResultsRef = useRef(new Map<string, PendingResult>());
	const pendingResultTimeoutIdsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
	const observationFrameIdRef = useRef<number | undefined>(undefined);
	const trackedKeysRef = useRef(new Set<string>());
	const observePendingResultsRef = useRef<() => void>(() => {});
	getCwrRemixEditorAccessRef.current = getCwrRemixEditorAccess;

	const schedulePendingResultObservation = useCallback(() => {
		if (observationFrameIdRef.current !== undefined) {
			return;
		}

		let ranSynchronously = false;
		const frameId = requestAnimationFrame(() => {
			ranSynchronously = true;
			observationFrameIdRef.current = undefined;
			observePendingResultsRef.current();
		});
		if (!ranSynchronously) {
			observationFrameIdRef.current = frameId;
		}
	}, []);

	const observePendingResults = useCallback(() => {
		const editorAccess = getCwrRemixEditorAccessRef.current?.();
		if (!editorAccess) {
			return;
		}

		const editorRoot = editorAccess.getEditorRoot();
		for (const [trackingKey, pendingResult] of pendingResultsRef.current) {
			const element = editorAccess.getResultElement(pendingResult.identity);
			if (!element) {
				continue;
			}

			pendingResultsRef.current.delete(trackingKey);
			const timeoutId = pendingResultTimeoutIdsRef.current.get(trackingKey);
			if (timeoutId !== undefined) {
				clearTimeout(timeoutId);
				pendingResultTimeoutIdsRef.current.delete(trackingKey);
			}
			const observer = new IntersectionObserver(
				(entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) {
						return;
					}
					observer.disconnect();
					observersRef.current.delete(trackingKey);
					if (claimResult(pendingResult.singleInstrumentationId, pendingResult.identity)) {
						pendingResult.onVisible();
					}
				},
				{
					root: editorRoot.closest<HTMLElement>('.fabric-editor-popup-scroll-parent') ?? editorRoot,
					threshold: 0.1,
				},
			);
			observersRef.current.set(trackingKey, observer);
			observer.observe(element);
		}

		if (pendingResultsRef.current.size === 0) {
			mutationObserverRef.current?.disconnect();
			mutationObserverRef.current = undefined;
			return;
		}

		if (typeof MutationObserver !== 'undefined' && !mutationObserverRef.current) {
			// Below-the-fold nodes may mount only after the user scrolls.
			mutationObserverRef.current = new MutationObserver(schedulePendingResultObservation);
			mutationObserverRef.current.observe(editorRoot, {
				childList: true,
				subtree: true,
			});
		}
	}, [schedulePendingResultObservation]);
	observePendingResultsRef.current = observePendingResults;

	useEffect(
		() => () => {
			observersRef.current.forEach((observer) => observer.disconnect());
			observersRef.current.clear();
			mutationObserverRef.current?.disconnect();
			mutationObserverRef.current = undefined;
			pendingResultsRef.current.clear();
			pendingResultTimeoutIdsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
			pendingResultTimeoutIdsRef.current.clear();
			if (observationFrameIdRef.current !== undefined) {
				cancelAnimationFrame(observationFrameIdRef.current);
				observationFrameIdRef.current = undefined;
			}
		},
		[],
	);

	return useCallback(
		(singleInstrumentationId: string, identity: InsertedContentIdentity, onVisible: () => void) => {
			if (typeof IntersectionObserver === 'undefined') {
				return;
			}

			const trackingKey = getTrackingKey(identity);
			// Deliberately identity-only: reusing the same visual must not count as a new result.
			if (trackedKeysRef.current.has(trackingKey)) {
				return;
			}
			trackedKeysRef.current.add(trackingKey);
			pendingResultsRef.current.set(trackingKey, {
				singleInstrumentationId,
				identity,
				onVisible,
			});
			pendingResultTimeoutIdsRef.current.set(
				trackingKey,
				setTimeout(() => {
					pendingResultTimeoutIdsRef.current.delete(trackingKey);
					if (!pendingResultsRef.current.delete(trackingKey)) {
						return;
					}
					trackedKeysRef.current.delete(trackingKey);
					if (pendingResultsRef.current.size === 0) {
						mutationObserverRef.current?.disconnect();
						mutationObserverRef.current = undefined;
					}
				}, PENDING_RESULT_TIMEOUT_MS),
			);
			schedulePendingResultObservation();
		},
		[schedulePendingResultObservation],
	);
};
